import * as THREE from "three";
import { Sfx } from "../audio/sfx";
import { assignKeys, normalizeKey, type KeyDeck } from "../math/keys";
import {
  THREAT,
  buildFactBag,
  makeQuestion,
  pickFact,
  recordMastery,
  type Difficulty,
  type Mastery,
  type Question,
  type WeightedFact,
} from "../math/questions";
import { Engine } from "./engine";
import { hitPoints, hullPips, padScore } from "./score";
import type { KeyMode } from "./save";
import {
  BREACH_Z,
  LANE_COLORS,
  LANE_X,
  PLAYER_Z,
  SPAWN_Z,
  Sparks,
  disposeGroup,
  makeBeam,
  makeEnemy,
  makeLabel,
  makePlayer,
} from "./ships";
import { World } from "./world";

export interface RunConfig {
  tables: number[];
  difficulty: Difficulty;
  deck: KeyDeck;
  squaresOnly: boolean;
  keyMode?: KeyMode;
}

interface Enemy {
  lane: number;
  value: number;
  key: string;
  correct: boolean;
  alive: boolean;
  group: THREE.Group;
  label: THREE.Sprite;
}

interface BeamFx {
  mesh: THREE.Mesh;
  age: number;
}

export interface RunSummary {
  score: number;
  best: number;
  hits: number;
  misses: number;
  leaked: number;
  streakMax: number;
  fastestMs: number;
  accuracy: number;
  wave: number;
  mastery: Map<string, Mastery>;
}

export class Session {
  readonly engine: Engine;
  readonly world: World;
  readonly sfx: Sfx;
  private player: THREE.Group;
  private sparks: Sparks;
  private enemies: Enemy[] = [];
  private beams: BeamFx[] = [];
  private bag: WeightedFact[] = [];
  private mastery = new Map<string, Mastery>();
  private question: Question | null = null;
  private spawnedAt = 0;
  private approachSec = 5.4;
  private hull = 4;
  private hullMax = 4;
  private score = 0;
  private streak = 0;
  private streakMax = 0;
  private hits = 0;
  private misses = 0;
  private leaked = 0;
  private fastestMs = Number.POSITIVE_INFINITY;
  private wave = 1;
  private solved = 0;
  private playing = false;
  private paused = false;
  private time = 0;
  private bankAngle = 0;
  private onHud: (() => void) | null = null;
  private onOver: ((summary: RunSummary) => void) | null = null;
  private config: RunConfig | null = null;
  private raycaster = new THREE.Raycaster();
  private lastShotAt = 0;
  private best = 0;

  constructor(engine: Engine, world: World, sfx: Sfx) {
    this.sfx = sfx;
    this.engine = engine;
    this.world = world;
    this.player = makePlayer();
    this.player.scale.setScalar(3.4);
    this.engine.scene.add(this.player);
    this.sparks = new Sparks(this.engine.scene);
  }

  setActive(active: boolean): void {
    this.player.visible = active;
    if (!active) {
      this.playing = false;
      this.paused = false;
      this.clearEnemies();
      this.question = null;
    }
  }

  bind(hud: () => void, over: (summary: RunSummary) => void): void {
    this.onHud = hud;
    this.onOver = over;
  }

  snapshot() {
    return {
      playing: this.playing,
      paused: this.paused,
      hull: this.hull,
      hullMax: this.hullMax,
      hullText: hullPips(this.hull, this.hullMax),
      score: this.score,
      scoreText: padScore(this.score),
      streak: this.streak,
      wave: this.wave,
      question: this.question,
      callout: this.callout,
    };
  }

  private callout = "";

  start(config: RunConfig, best = 0): void {
    this.clearEnemies();
    this.config = config;
    this.best = best;
    this.bag = buildFactBag(config.tables, config.squaresOnly);
    this.mastery = new Map();
    this.hullMax = THREAT[config.difficulty].hull;
    this.hull = this.hullMax;
    this.approachSec = THREAT[config.difficulty].approachSec;
    this.score = 0;
    this.streak = 0;
    this.streakMax = 0;
    this.hits = 0;
    this.misses = 0;
    this.leaked = 0;
    this.fastestMs = Number.POSITIVE_INFINITY;
    this.wave = 1;
    this.solved = 0;
    this.playing = true;
    this.paused = false;
    this.callout = "WEAPONS FREE";
    this.spawnQuestion();
    this.onHud?.();
  }

  abort(): void {
    this.playing = false;
    this.paused = false;
    this.clearEnemies();
    this.question = null;
    this.callout = "";
    this.onHud?.();
  }

  setPaused(paused: boolean): void {
    if (!this.playing) return;
    this.paused = paused;
    this.onHud?.();
  }

  handleKey(raw: string): void {
    if (!this.playing || this.paused) return;
    const key = normalizeKey(raw);
    if (key.length !== 1 || key < "a" || key > "z") return;
    const enemy = this.enemies.find((e) => e.alive && e.key === key);
    if (!enemy) {
      this.sfx.ui();
      this.callout = "NO LOCK";
      this.onHud?.();
      return;
    }
    this.fireEnemy(enemy);
  }

  fireLane(lane: number): boolean {
    if (!this.playing || this.paused) return false;
    const now = performance.now();
    if (now - this.lastShotAt < 160) return false;
    const enemy = this.enemies.find((e) => e.alive && e.lane === lane);
    if (!enemy) return false;
    this.lastShotAt = now;
    this.fireEnemy(enemy);
    return true;
  }

  pickLane(clientX: number, clientY: number): number | null {
    const canvas = this.engine.renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(new THREE.Vector2(x, y), this.engine.camera);
    const live = this.enemies.filter((e) => e.alive);
    const hits = this.raycaster.intersectObjects(
      live.map((e) => e.group),
      true,
    );
    if (hits[0]) {
      let obj: THREE.Object3D | null = hits[0].object;
      while (obj) {
        const match = live.find((e) => e.group === obj);
        if (match) return match.lane;
        obj = obj.parent;
      }
    }
    const point = new THREE.Vector3();
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    if (!this.raycaster.ray.intersectPlane(plane, point)) return null;
    if (point.z > 16 || point.z < SPAWN_Z - 4) return null;
    let bestLane = 0;
    let best = Infinity;
    for (let i = 0; i < LANE_X.length; i++) {
      const d = Math.abs(point.x - LANE_X[i]);
      if (d < best) {
        best = d;
        bestLane = i;
      }
    }
    return best < 4.6 ? bestLane : null;
  }

  private fireEnemy(enemy: Enemy): void {
    const q = this.question;
    if (!q) return;
    this.lastShotAt = performance.now();
    this.sfx.laser();
    this.bankAngle = enemy.lane < 2 ? 0.35 : -0.35;
    const from = this.player.position.clone().add(new THREE.Vector3(0, 0.2, -1.2));
    const to = enemy.group.position.clone();
    const beam = makeBeam(from, to, LANE_COLORS[enemy.lane]);
    this.engine.scene.add(beam);
    this.beams.push({ mesh: beam, age: 0 });

    const reactionMs = (this.engine.clock.elapsedTime - this.spawnedAt) * 1000;
    if (enemy.correct) {
      this.resolveHit(q, reactionMs, enemy);
    } else {
      this.resolveMiss(q, enemy);
    }
  }

  private resolveHit(q: Question, reactionMs: number, enemy: Enemy): void {
    this.destroyEnemy(enemy, true);
    this.hits += 1;
    this.streak += 1;
    this.streakMax = Math.max(this.streakMax, this.streak);
    this.fastestMs = Math.min(this.fastestMs, reactionMs);
    const pts = hitPoints(reactionMs, this.approachSec * 1000, this.streak);
    this.score += pts.total;
    recordMastery(this.mastery, q.a, q.b, true, reactionMs);
    this.sfx.correct();
    this.sfx.explode();
    if (this.streak > 0 && this.streak % 5 === 0) this.sfx.combo(this.streak / 5);
    this.callout = pts.rapid ? "INSTINCT LOCK" : this.streak >= 5 ? `OVERCLOCK x${this.streak}` : "PRODUCT CONFIRMED";
    this.solved += 1;
    if (this.solved % 8 === 0) {
      this.wave += 1;
      this.approachSec = Math.max(2.2, this.approachSec * 0.92);
    }
    for (const extra of this.enemies) {
      if (extra.alive) this.destroyEnemy(extra, false);
    }
    this.flash("good");
    this.spawnQuestion();
    this.onHud?.();
  }

  private resolveMiss(q: Question, enemy: Enemy): void {
    this.destroyEnemy(enemy, true);
    this.misses += 1;
    this.streak = 0;
    this.hull = Math.max(0, this.hull - 1);
    recordMastery(this.mastery, q.a, q.b, false, 4000);
    this.sfx.wrong();
    this.sfx.explode();
    this.callout = "FALSE PRODUCT";
    this.flash("bad");
    this.engine.addShake(0.35);
    this.onHud?.();
    if (this.hull <= 0) this.gameOver();
  }

  private destroyEnemy(enemy: Enemy, burst: boolean): void {
    if (!enemy.alive) return;
    enemy.alive = false;
    if (burst) this.sparks.burst(enemy.group.position.clone(), LANE_COLORS[enemy.lane]);
    this.engine.scene.remove(enemy.group);
    disposeGroup(enemy.group);
  }

  private spawnQuestion(): void {
    if (!this.config || !this.playing) return;
    this.clearEnemies();
    const keys = assignKeys(4, this.config.deck);
    const fact = pickFact(this.bag, this.mastery);
    const q = makeQuestion(fact, keys);
    this.question = q;
    this.world.setQuestion(q.a, q.b);
    this.spawnedAt = this.engine.clock.elapsedTime;
    this.enemies = q.choices.map((choice) => {
      const group = makeEnemy(choice.lane);
      const color = `#${LANE_COLORS[choice.lane].toString(16).padStart(6, "0")}`;
      const label = makeLabel(choice.key, choice.value, color);
      group.add(label);
      group.position.x = LANE_X[choice.lane];
      this.engine.scene.add(group);
      return {
        lane: choice.lane,
        value: choice.value,
        key: choice.key,
        correct: choice.correct,
        alive: true,
        group,
        label,
      };
    });
  }

  private clearEnemies(): void {
    for (const e of this.enemies) {
      if (!e.group.parent) continue;
      this.engine.scene.remove(e.group);
      disposeGroup(e.group);
    }
    this.enemies = [];
  }

  private flash(kind: "good" | "bad"): void {
    const el = document.getElementById("flash");
    if (!el) return;
    el.classList.toggle("good", kind === "good");
    el.classList.add("on");
    window.setTimeout(() => el.classList.remove("on"), 90);
  }

  private gameOver(): void {
    this.playing = false;
    this.sfx.alarm();
    this.clearEnemies();
    const attempts = this.hits + this.misses + this.leaked;
    const summary: RunSummary = {
      score: this.score,
      best: Math.max(this.best, this.score),
      hits: this.hits,
      misses: this.misses,
      leaked: this.leaked,
      streakMax: this.streakMax,
      fastestMs: Number.isFinite(this.fastestMs) ? this.fastestMs : 0,
      accuracy: attempts === 0 ? 0 : this.hits / attempts,
      wave: this.wave,
      mastery: this.mastery,
    };
    this.onOver?.(summary);
  }

  simulate(dt: number, time: number): void {
    this.time = time;
    this.sparks.update(dt);

    this.bankAngle += (0 - this.bankAngle) * Math.min(1, dt * 4);
    this.player.rotation.z = this.bankAngle;
    this.player.position.y = 0.8 + Math.sin(this.time * 3.4) * 0.08;
    this.player.position.z = PLAYER_Z;

    for (const beam of this.beams) {
      beam.age += dt;
      const mat = beam.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = Math.max(0, 1 - beam.age / 0.12);
    }
    this.beams = this.beams.filter((b) => {
      if (b.age < 0.12) return true;
      this.engine.scene.remove(b.mesh);
      b.mesh.geometry.dispose();
      (b.mesh.material as THREE.Material).dispose();
      return false;
    });

    if (this.playing && !this.paused) {
      const speed = (BREACH_Z - SPAWN_Z) / this.approachSec;
      for (const enemy of this.enemies) {
        if (!enemy.alive) continue;
        enemy.group.position.z += speed * dt;
        enemy.group.rotation.y += dt * 1.6;
        enemy.group.rotation.x += dt * 0.7;
        enemy.group.position.y = 1.2 + Math.sin(this.time * 4 + enemy.lane) * 0.15;
        if (enemy.group.position.z >= BREACH_Z) {
          if (enemy.correct) {
            this.leaked += 1;
            this.streak = 0;
            this.hull = Math.max(0, this.hull - 1);
            this.sfx.alarm();
            this.callout = "BREACH";
            this.flash("bad");
            this.engine.addShake(0.6);
            if (this.question) recordMastery(this.mastery, this.question.a, this.question.b, false, 8000);
            this.onHud?.();
            if (this.hull <= 0) {
              this.gameOver();
              break;
            }
            this.spawnQuestion();
            this.onHud?.();
            break;
          } else {
            this.destroyEnemy(enemy, false);
          }
        }
      }
    }
  }
}
